<template>
    <Teleport to="body">
        <div v-if="shouldRender" class="global-support-fab" :data-test="dataTest">
            <v-btn
                icon
                size="large"
                color="primary"
                elevation="6"
                class="global-support-fab__btn"
                :aria-label="ariaLabel"
                :title="ariaLabel"
                @click="onClick"
            >
                <MessageCircleWarning :size="22" />
            </v-btn>

            <!-- Бейдж намеренно СНАРУЖИ v-btn: у Vuetify на .v-btn стоит
                 overflow:hidden под ripple, плюс border-radius 50% — внутри
                 кнопки бейдж срезался бы. pointer-events:auto нужен, потому
                 что у контейнера они выключены, иначе тап проваливается
                 в нижнюю навигацию. -->
            <span class="global-support-fab__beta" aria-hidden="true" @click="onClick"> BETA </span>

            <CreateTicketModal
                v-model="showModal"
                :context="null"
                :preset-category="undefined"
                @created="onCreated"
                @duplicate="onDuplicate"
            />
        </div>
    </Teleport>
</template>

<script setup lang="ts">
/**
 * GlobalSupportFab — глобальный плавающий FAB справа внизу на всех
 * страницах сайта (кроме админки/auth/landing/in-call).
 *
 * Это «всегда видимая» альтернатива контекстной <ReportProblemButton>:
 * на consultation/meeting страницах живёт ReportProblemButton с
 * автоконтекстом, а здесь — простой ввод без контекста для общих
 * обращений (вопросы об аккаунте, бронированиях, оплате и т.п.).
 *
 * Условия отображения:
 *   - featureFlag VUE_APP_SUPPORT_REPORT_BUTTON_ENABLED === 'true'
 *   - пользователь авторизован
 *   - текущий путь не в EXCLUDED_PREFIXES (админка, auth, лэндинги, видеовстреча)
 */
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { MessageCircleWarning } from 'lucide-vue-next';
import { emitToast } from '@/shared/lib/toast';
import CreateTicketModal from './CreateTicketModal.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();

const showModal = ref<boolean>(false);
const dataTest = 'global-support-fab';
// Пометка беты входит в aria-label: aria-label на кнопке перекрывает её
// содержимое, а сам бейдж скрыт от скринридера как декоративный.
const ariaLabel = 'Сообщить о проблеме (бета)';

/**
 * Пути, на которых FAB прячется:
 *  - /admin/* — операторская/админская зона
 *  - /login, /register — auth
 *  - /MeetingPage, /conference, /meeting, /booking-conference, /event-conference —
 *    видеовстречи (там полноэкранный UI и контекстная ReportProblemButton)
 *  - /support — там пользователь уже в саппорте, дублировать FAB не нужно
 *  - корень "/" и лэндинги Home/SuperpeerLanding — landing pages, чтобы не
 *    мешать первой странице
 */
const EXCLUDED_PREFIXES = [
    '/admin',
    '/login',
    '/register',
    '/MeetingPage',
    '/conference',
    '/booking-conference',
    '/event-conference',
    '/support',
] as const;

const EXCLUDED_EXACT = ['/', '/Home', '/NewLandingMentor', '/SuperpeerLanding'] as const;

const featureFlagEnabled = computed<boolean>(() => {
    // Прямой `process.env.X` — webpack DefinePlugin подменит литералом.
    // globalThis-cast в browser runtime даёт undefined и сбивает webpack.
    const raw = process.env.VUE_APP_SUPPORT_REPORT_BUTTON_ENABLED ?? '';
    return String(raw).toLowerCase() === 'true';
});

const isAuthenticated = computed<boolean>(() => Boolean(store.getters?.isAuthenticated));

const isExcludedPath = computed<boolean>(() => {
    const path = route.path;
    if (EXCLUDED_EXACT.includes(path as (typeof EXCLUDED_EXACT)[number])) return true;
    return EXCLUDED_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
});

const shouldRender = computed<boolean>(
    () => featureFlagEnabled.value && isAuthenticated.value && !isExcludedPath.value,
);

const onClick = (): void => {
    showModal.value = true;
};

const onCreated = (_payload: { publicId: string; subject: string }): void => {
    // CreateTicketModal уже показал toast про создание; повторно не дублируем.
};

const onDuplicate = (payload: { publicId: string; subject: string }): void => {
    emitToast('У вас уже есть обращение по этому вопросу.', 'info');
    router.push({ name: 'support-ticket', params: { publicId: payload.publicId } }).catch(() => {
        /* ignore — навигация может не пройти, тоста достаточно */
    });
};
</script>

<style scoped>
.global-support-fab {
    position: fixed;
    /* Над z-индексом обычного контента, ниже модалок Vuetify (2400) */
    z-index: 2000;
    right: 24px;
    /* iOS safe-area: оставляем место для home-indicator */
    bottom: calc(24px + env(safe-area-inset-bottom, 0px));
    pointer-events: none;
}

.global-support-fab__btn {
    pointer-events: auto;
    border-radius: 50% !important;
    width: 56px;
    height: 56px;
    min-width: 0;
}

.global-support-fab__beta {
    position: absolute;
    /* В силуэте круга: за его пределы бейдж не вылезает, чтобы не ловить
       тапы, адресованные соседним элементам. */
    top: -2px;
    right: -2px;
    pointer-events: auto;
    /* Тёмный текст на белом вместо белого на #7B65F7 (тот давал 4.16:1 —
       ниже AA для 10px). */
    background: #ffffff;
    color: #4c1d95;
    border-radius: 8px;
    padding: 1px 5px;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.4px;
    line-height: 1.4;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    cursor: pointer;
    user-select: none;
}

/* На совсем узких экранах сдвигаем чуть ниже — обычно там нижняя навигация */
@media (max-width: 600px) {
    .global-support-fab {
        right: 16px;
        bottom: calc(80px + env(safe-area-inset-bottom, 0px));
    }
    .global-support-fab__btn {
        width: 48px;
        height: 48px;
    }
}
</style>
